/* FilterControls.jsx — the shared search/filter layer for the Audio and Video
 * archives: the Filters toggle, the collapsible panel, a themed dropdown that
 * replaces the browser's unstylable <select> popup, the active-filter pills and
 * the empty state.
 *
 * The panel is field-driven, so each page supplies its own list of dropdowns:
 *
 *   <FilterPanel
 *     fields={[{ key: "year", label: "Year", icon: <CalIc />,
 *                placeholder: "Any year", options: facets.years }, …]}
 *     values={filters} onChange={setFilter} … />
 *
 * Styling lives in filters.css, imported once here.
 */

import { useState, useRef, useEffect } from "react";
import "./filters.css";

// ─── Icons ───────────────────────────────────────────────────────────────────

export function CaretIc({ size = 15 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 9.5l6 6 6-6" />
    </svg>
  );
}

export function ChevUpIc({ size = 17 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 14.5l6-6 6 6" />
    </svg>
  );
}

export function SlidersIc({ size = 17 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
    >
      <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
      <circle cx="16" cy="7" r="2.2" />
      <circle cx="10" cy="17" r="2.2" />
    </svg>
  );
}

export function CalIc({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3.5" y="5" width="17" height="16" rx="3" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
    </svg>
  );
}

export function MoonIc({ size = 15 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}

export function PinIc({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 21s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.6" />
    </svg>
  );
}

export function XIc({ size = 11 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function SearchGlyph({ size = 34 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <circle cx="11" cy="11" r="7.5" />
      <line x1="21" y1="21" x2="16.8" y2="16.8" />
    </svg>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// A date input's year spinner takes partial entry, so "226" silently becomes
// year 0226. Bounds the range pickers to something plausible.
export const DATE_MIN = "1900-01-01";
export const DATE_MAX = "2100-12-31";

// "2026-08-19" → "Aug 19, 2026" (for the active-filter pills)
export function prettyDate(value) {
  if (!value) return "";
  const [y, m, d] = value.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    timeZone: "UTC",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// ─── Dropdown ────────────────────────────────────────────────────────────────

// A native <select> renders its popup with OS chrome that can't be themed, so
// the list is built here instead. `placeholder`, when given, becomes the first
// row and clears the field.
export function Dropdown({
  variant = "field",
  value,
  onChange,
  options,
  placeholder,
  icon,
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef(null);

  const items = placeholder
    ? [{ value: "", label: placeholder }, ...options]
    : options;
  const selected = items.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  function openMenu() {
    setActiveIndex(items.findIndex((o) => o.value === value));
    setOpen(true);
  }

  function pick(next) {
    onChange(next);
    setOpen(false);
  }

  function onKeyDown(e) {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (items[activeIndex]) pick(items[activeIndex].value);
    }
  }

  const menu = open && (
    <ul className="flt-menu" role="listbox">
      {items.map((o, i) => (
        <li
          key={o.value || "__any"}
          role="option"
          aria-selected={o.value === value}
          className={`flt-menu-item ${
            o.value === value ? "is-selected" : ""
          } ${i === activeIndex ? "is-active" : ""}`}
          onMouseEnter={() => setActiveIndex(i)}
          // keep focus on the trigger so keyboard nav survives a mouse hover
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => pick(o.value)}
        >
          {o.label}
        </li>
      ))}
    </ul>
  );

  const trigger = (
    <button
      type="button"
      className={`flt-trigger ${variant === "inline" ? "flt-trigger--inline" : ""}`}
      onClick={() => (open ? setOpen(false) : openMenu())}
      onKeyDown={onKeyDown}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-label={ariaLabel}
    >
      <span className={selected && selected.value ? "" : "ph"}>
        {selected ? selected.label : placeholder}
      </span>
    </button>
  );

  if (variant === "inline") {
    return (
      <span className="flt-sort" ref={rootRef}>
        {trigger}
        <span className="cv">
          <CaretIc size={14} />
        </span>
        {menu}
      </span>
    );
  }

  return (
    <div
      className={`flt-ctl ${value ? "flt-ctl--set" : ""} ${open ? "flt-ctl--open" : ""}`}
      ref={rootRef}
    >
      <span className="lic">{icon}</span>
      {trigger}
      <span className="cv">
        <CaretIc size={16} />
      </span>
      {menu}
    </div>
  );
}

// ─── Fields ──────────────────────────────────────────────────────────────────

function SelField({ label, icon, value, onChange, placeholder, options }) {
  return (
    <div className="flt-f">
      <label>{label}</label>
      <Dropdown
        icon={icon}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        ariaLabel={label}
      />
    </div>
  );
}

function DateField({ label, value, onChange, min, max }) {
  return (
    <div className="flt-range-card">
      <span>{label}</span>
      <div className="flt-ctl">
        <span className="lic">
          <CalIc size={15} />
        </span>
        <input
          type="date"
          value={value}
          // falls back to the sane range so a half-typed year can't quietly
          // filter everything out
          min={min || DATE_MIN}
          max={max || DATE_MAX}
          onChange={(e) => onChange(e.target.value)}
          onClick={(e) => {
            // Open the picker from anywhere in the field, not just the glyph.
            if (e.currentTarget.showPicker) {
              try {
                e.currentTarget.showPicker();
              } catch (_) {
                /* unsupported or not user-activated — the field still works */
              }
            }
          }}
        />
      </div>
    </div>
  );
}

// ─── Public pieces ───────────────────────────────────────────────────────────

export function FilterToggle({ open, onToggle, count = 0 }) {
  return (
    <button
      type="button"
      className={`flt-toggle ${open ? "flt-toggle--open" : ""}`}
      onClick={onToggle}
      aria-expanded={open}
      aria-label="Filters"
    >
      <SlidersIc />
      <span className="lbl">Filters</span>
      {count > 0 && <span className="badge">{count}</span>}
      <span className="cv">
        <CaretIc size={15} />
      </span>
    </button>
  );
}

export function FilterPanel({
  fields,
  values,
  onChange,
  onReset,
  onClose,
  total,
  columns = 4,
  fromKey = "from",
  toKey = "to",
}) {
  return (
    <div className="flt-panel">
      <button
        className="flt-collapse"
        onClick={onClose}
        aria-label="Collapse filters"
      >
        <ChevUpIc />
      </button>

      <div className={`flt-grid flt-grid--${columns}`}>
        {fields.map((f) => (
          <SelField
            key={f.key}
            label={f.label}
            icon={f.icon}
            value={values[f.key] || ""}
            onChange={(v) => onChange(f.key, v)}
            placeholder={f.placeholder}
            options={f.options || []}
          />
        ))}
      </div>

      <div className="flt-range">
        <div className="flt-f" style={{ gap: 10 }}>
          <label>Date Range (Optional)</label>
          <div className="flt-range-grid">
            <DateField
              label="From"
              value={values[fromKey] || ""}
              onChange={(v) => onChange(fromKey, v)}
              max={values[toKey] || undefined}
            />
            <DateField
              label="To"
              value={values[toKey] || ""}
              onChange={(v) => onChange(toKey, v)}
              min={values[fromKey] || undefined}
            />
          </div>
        </div>
      </div>

      <div className="flt-panel-foot">
        <button className="flt-link" onClick={onReset}>
          Clear all
        </button>
        <button className="flt-apply" onClick={onClose}>
          Show {total} result{total === 1 ? "" : "s"}
        </button>
      </div>
    </div>
  );
}

export function ActivePills({ pills, onRemove, onReset }) {
  if (!pills.length) return null;
  return (
    <div className="flt-active">
      {pills.map((p) => (
        <span key={p.key} className="flt-pill">
          {p.k && <span className="k">{p.k}</span>}
          {p.label}
          <button
            onClick={() => onRemove(p.key)}
            aria-label={`Remove ${p.k ? `${p.k} ` : ""}${p.label} filter`}
          >
            <XIc />
          </button>
        </span>
      ))}
      <button className="flt-clear" onClick={onReset}>
        Clear all
      </button>
    </div>
  );
}

export function EmptyState({
  onReset,
  title = "Nothing in the archive matches",
  message = "Try a different month, widen the type, or clear a filter or two.",
}) {
  return (
    <div className="flt-empty">
      <span className="ic">
        <SearchGlyph />
      </span>
      <h3>{title}</h3>
      <p>{message}</p>
      <button className="flt-apply" onClick={onReset}>
        Clear all filters
      </button>
    </div>
  );
}
